import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";

//Components
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CssBaseline,
  Typography,
  Box,
} from "@material-ui/core";
import IncidentHeader from "./IncidentHeader/IncidentHeader";
import InitialCallInformation from "./InitialCallInformation";
import PeopleDetail from "./PersonDetail/PeopleDetail";
import VehiclesDetail from "./VehiclesDetail";
import IncidentDetail from "./IncidentDetail";
import ArrestInformationTabs from "./ArrestInformationTabs";
import TicketsAndWarningsTabs from "./TicketsAndWarningsTabs";
import IncidentReview from "./IncidentReview";
import DmvQueryModal from "./DMVQueryModal/DmvQueryModal";

//Styling
import { withStyles } from "@material-ui/core/styles";
import { useStyles } from "../Styles/styles";

//Contexts
import { UserContext } from "../Contexts/UserContext";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { SetupContext } from "../Contexts/SetupContext";
import { DashboardContext } from "../Contexts/DashboardContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { ACTIVE_INCIDENTS_ACTIONS } from "../Actions/ActiveIncidentsActions";

//Fetching
import { useMutation, useLazyQuery, useQuery } from "@apollo/client";
import { REMOVE_OFFICER_FROM_INCIDENT } from "../Mutations/RemoveOfficerFromIncident";
import { ADD_OR_UPDATE_OFFICER_IN_INCIDENT } from "../Mutations/AddOrUpdateOfficerInIncident";
import { SET_OFFICER_INCIDENT_TIMES } from "../Mutations/SetOfficerIncidentTimes";
import { CANCEL_INCIDENT } from "../Mutations/CancelIncident";
import { PISS_FOR_INCIDENT } from "../Queries/PissForIncident";
import { GET_VEHICLE_INCIDENT_SPECIFICS } from "../Queries/GetVehicleIncidentSpecifics";
import { GET_INCIDENT_DETAIL } from "../Queries/GetIncidentDetail";
import { GET_ARRESTS } from "../Queries/GetArrests";
// import { GET_PROPERTIES } from '../Queries/GetProperties'
import { GET_TICKETS } from "../Queries/GetTickets";
import { GET_INCIDENT_REVIEW } from "../Queries/GetIncidentReview";
import { DISPATCHABLE_OFFICERS } from "../Queries/DispatchableOfficers";
import { SPECIFIC_SETUP_LISTS_QUERY } from "../Queries/SetupListsQuery";
import HelpIconButton from "./HelpIconButton";

//Utils
import {
  handleUuid,
  localStoreData,
  personPanelHeader,
  vehiclePanelHeader,
  arrestPanelHeader,
  ticketPanelHeader,
  reviewPanelHeader,
  updateOfficerTimesInOis,
  localGetData,
  returnLockedTicket,
  findPrimaryOfficer,
  parseIncidentSubscriptionMessage,
  parseQuerySubscriptionMessage,
  parseNarrativeSubscriptionMessage,
} from "../utils/utils";
import { createEmptyArrestCharge, emptyOis } from "../utils/EmptyObjects";
import { MutationContext } from "../Contexts/MutationContext";
import { checkPermission } from "../utils/Roles";
import {
  hasSealedIncidentRecord,
  SEALED_INCIDENT_BANNER_MESSAGE,
} from "../utils/sealedIncident";
import { GET_NARRATIVE_TEMPLATES } from "../Queries/GetTemplateNarratives";
import _ from "lodash";

//sounds
import tick from "../sounds/Tick.m4a";
import warning from "../sounds/warning.ogg";
import AirplaneDing from "../sounds/AirplaneDing.m4a";
import SlideWhistle from "../sounds/SlideWhistle.m4a";
import Badoo from "../sounds/Badoo.m4a";
import PoliceSiren from "../sounds/PoliceSiren.m4a";
import { IncidentSubscriptionContext } from "../Contexts/IncidentSubscriptionContext";
import { AVAILABLE_OFFICER_ACTIONS } from "../Actions/AvailableOfficerActions";
import SizeAndWeightFrame from "./SizeAndWeightFrame/Components/SizeAndWeightFrame";
import LazyFetchComponent from "./LazyFetchComponent/LazyFetchComponent";
import { GET_SIZE_AND_WEIGHT } from "./SizeAndWeightFrame/Query/SizeAndWeightQuery";
import { ADD_OR_UPDATE_SIZE_AND_WEIGHT_IN_INCIDENT } from "./SizeAndWeightFrame/Mutations/AddOrUpdateSizeAndWeightInIncident";
import { FLIPPER_ENABLED } from "../Queries/FlipperEnabled";
import { SingleIncidentContext } from "../Contexts/SingleIncidentContext";

import moment from "moment";

const StyledAccordion = withStyles({
  root: {
    border: "1px solid rgba(0, 0, 0, .125)",
    color: props =>
      props.nightmode === "true"
        ? props.expanded
          ? "white"
          : "#bbbbbb"
        : props.expanded
          ? "#303e4f"
          : "#6096d1",
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "0",
    },
  },
  expanded: {},
})(Accordion);

const StyledAccordionSummary = withStyles({
  root: {
    backgroundColor: props =>
      props.nightmode === "true" ? "#616161" : "#fae8ad",
    // borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  content: {
    "&$expanded": {
      margin: "0",
    },
  },
  expanded: {},
})(AccordionSummary);

const StyledAccordionDetails = withStyles({
  root: {
    padding: 0,
  },
})(AccordionDetails);

const SealedIncidentBanner = () => (
  <Box
    data-cy="sealed-incident-banner"
    style={{
      position: "sticky",
      top: 0,
      zIndex: 1200,
      width: "100%",
      backgroundColor: "#b00020",
      color: "#ffffff",
      padding: "10px 16px",
      textAlign: "center",
      boxSizing: "border-box",
    }}
  >
    <Typography
      component="div"
      style={{
        fontWeight: 700,
        fontSize: 14,
        lineHeight: 1.35,
        letterSpacing: 0,
      }}
    >
      {SEALED_INCIDENT_BANNER_MESSAGE}
    </Typography>
  </Box>
);

const IncidentPanels = ({
  openChat,
  incident,
  incidentIndex,
  handlePersonOpening,
  handleIncidentOpening,
  handleTicketOpening,
  handleVehicleOpening,
  getCallInfo,
  subscriptionServer,
  chatWindowRef,
  setPausePolling,
  userPreferences,
  setUserPreferences,
  setPopper,
  socketConnected = false
}) => {
  const classes = useStyles();
  const {
    user,
    responseErrorHandler,
    nightMode,
    onlineState,
    sizeAndWeightEnabledForUser,
  } = useContext(UserContext);
  const { setup, setSpecificSetup } = useContext(SetupContext);
  const { activeIncidentDispatch, availableOfficerDispatch, setAvailOfficer } =
    useContext(DashboardContext);
  const { UPDATE_AVAILABLE_OFFICER } = AVAILABLE_OFFICER_ACTIONS;
  const {
    initialCallInfoSub,
    dispatchNarrativeSub,
    personIncidentSpecificSub,
    vehicleDetailSub,
    incidentDetailSub,
    arrestAndIncidentDetailSub,
    ticketFrameSub,
    incidentReviewSub,
  } = useContext(IncidentSubscriptionContext);
  const { yoeJuvenileStatuses } = useContext(SingleIncidentContext);
  const { REMOVE_ACTIVE_INCIDENT } = ACTIVE_INCIDENTS_ACTIONS;
  const { incidentNumber } = incident;
  const { incidents, incidentDispatch, incidentPanel, setIncidentPanel } =
    useContext(IncidentsContext);
  const {
    EXTEND_PIS,
    UPDATE_INCIDENT,
    UPDATE_INCIDENT_DETAIL,
    UPDATE_INCIDENT_REVIEW,
  } = INCIDENT_ACTIONS;
  const {
    updateTicketAllFields,
    updateIncidentDetail,
    updateIncidentReview,
    setStatus,
    updateCallTeam,
  } = useContext(MutationContext);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [incidentPeople, setIncidentPeople] = useState([]);
  const [incidentOfficers, setIncidentOfficers] = useState([]);

  const _initialCallInfoIsMounted = useRef(false);
  const _peopleDetailIsMounted = useRef(false);
  const _vehicleDetailIsMounted = useRef(false);
  const _incidentDetailIsMounted = useRef(false);
  const _arrestInformationIsMounted = useRef(false);
  const _ticketAndWarningIsMounted = useRef(false);
  const _incidentReviewIsMounted = useRef(false);

  const memoizedCanEditIncident = useMemo(
    () =>
      checkPermission(
        user?.roleNames,
        "edit_incident",
        user?.editableOriIds,
        incident?.ori?.id,
        incident?.id,
      ),
    [user?.roleNames, user?.editableOriIds, incident?.id, incident?.ori],
  );
  const memoizedCanEditOfficerNarrative = useMemo(
    () =>
      checkPermission(
        user?.roleNames,
        "edit_officer_narratives",
        user?.editableOriIds,
        incident?.ori?.id,
        incident?.id,
      ),
    [user?.roleNames, user?.editableOriIds, incident?.id, incident?.ori],
  );
  const memoizedIsOnIncident = useMemo(() => {
    return incident?.officerIncidentSpecifics?.findIndex(
      ois => ois?.officer?.id?.toString() === user?.id?.toString(),
    ) > -1
      ? true
      : false;
  }, [incident?.officerIncidentSpecifics, user?.id]);
  const memoizedCanSubmitToJb = useMemo(
    () =>
      checkPermission(
        user?.roleNames,
        "submit_to_jb",
        user?.editableOriIds,
        incident?.ori?.id,
        incident?.id,
      ),
    [user?.roleNames, user?.editableOriIds, incident?.id, incident?.ori],
  );

  const [swFlipper, { data: swFlipperData }] = useLazyQuery(FLIPPER_ENABLED);

  useEffect(() => {
    if (user?.id && incident?.ori?.id) {
      swFlipper({
        variables: {
          originatorId: user?.id,
          feature: "size-and-weight",
          oriId: incident?.ori?.id,
        },
      });
    }
  }, [swFlipper, incident?.ori?.id, user?.id]);

  const sizeAndWeightEnabledForIncident = useMemo(
    () => swFlipperData?.[Object.keys(swFlipperData)[0]]?.enabled,
    [swFlipperData],
  );
  const [axonFlipper, { data: axonFlipperData }] =
    useLazyQuery(FLIPPER_ENABLED);

  useEffect(() => {
    if (user?.id && incident?.ori?.id) {
      axonFlipper({
        variables: {
          originatorId: user?.id,
          feature: "axon",
          oriId: incident?.ori?.id,
        },
      });
    }
  }, [axonFlipper, incident?.ori?.id, user?.id]);

  const axonEnabledForIncident = useMemo(
    () => axonFlipperData?.[Object.keys(axonFlipperData)[0]]?.enabled,
    [axonFlipperData],
  );

  const handleQuerySubscription = useCallback(
    m => {
      // console.log("handleQuerySubscription in IncidentPanels was sent: ", m);
      const response = parseQuerySubscriptionMessage(m);
      const { type } = response || "";
      const id =
        type === "officerId" ? response?.officerId : response?.incidentId;
      const isIncident = id?.toString() === incident?.id?.toString();
      if (type === "incidentId" && isIncident) {
        //get the qr for the incident
        const frameObj = {
          ref: _initialCallInfoIsMounted.current,
          query: initialCallInfoSub,
        };
        frameObj?.query({ variables: { incidentId: incident?.id } });
      } else {
        //it's an officer emit, ignore it
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [incident?.id, user?.id],
  );

  async function playAudio(file) {
    try {
      await new Audio(file).play();
    } catch (err) {
      console.log(err);
    }
  }

  const handleIncidentSubscription = useCallback(
    m => {
      const FRAME_LOOKUP = {
        call_info_header: {
          ref: _initialCallInfoIsMounted.current,
          query: initialCallInfoSub,
        },
        dispatch_narrative: {
          ref: _initialCallInfoIsMounted.current,
          query: dispatchNarrativeSub,
        },
        person_header: {
          ref: _peopleDetailIsMounted.current,
          query: personIncidentSpecificSub,
        },
        vehicle_header: {
          ref: _vehicleDetailIsMounted.current,
          query: vehicleDetailSub,
        },
        incident_detail_header: {
          ref: _incidentDetailIsMounted.current,
          query: incidentDetailSub,
        },
        incident_arrest_header: {
          ref: _arrestInformationIsMounted.current,
          query: () => arrestAndIncidentDetailSub(incident?.id),
        },
        ticket_header: {
          ref: _ticketAndWarningIsMounted.current,
          query: ticketFrameSub,
        },
        incident_review_header: {
          ref: _incidentReviewIsMounted.current,
          query: () =>
            incidentReviewSub({
              variables: { incidentReviewId: incident?.incidentReview?.id },
            }),
        },
      };

      // console.log("handleIncidentSubscription was sent: ", m);
      const { incidentId, frame, originatorId, app } =
        parseIncidentSubscriptionMessage(m);
      const fromCore = app === "core";
      const isIncident = incidentId?.toString() === incident?.id?.toString();
      const isNotSelf = originatorId?.toString() !== user?.id?.toString();
      const frameObj = FRAME_LOOKUP?.[frame] || null;

      if (isIncident && (isNotSelf || fromCore) && frameObj?.ref) {
        if (frameObj?.query) {
          // Default: refetch the whole frame
          frameObj?.query({ variables: { incidentId: incident?.id } });
        }
        if (
          userPreferences?.incidentUpdateSound !== "false" &&
          frame === "call_info_header"
        ) {
          switch (userPreferences?.incidentUpdateSound) {
            case "tick":
              playAudio(tick);
              return null;

            case "warning":
              playAudio(warning);

              return null;

            case "AirplaneDing":
              playAudio(AirplaneDing);

              return null;

            case "SlideWhistle":
              playAudio(SlideWhistle);

              return null;
            case "Badoo":
              playAudio(Badoo);

              return null;
            case "PoliceSiren":
              playAudio(PoliceSiren);

              return null;

            default:
              return null;
          }
        } else {
          // console.log("Audio notification disabled");
        }
      } else {
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      incident?.id,
      incident?.incidentReview?.id,
      user?.id,
      userPreferences?.incidentUpdateSound,
    ],
  );

  //add clean up to cancel throttle when the initial call frame unmounts?
  useEffect(() => {
    if (onlineState) {
      subscriptionServer.subscribeToIncident(
        incident?.id,
        _.throttle(handleIncidentSubscription, 5000, {
          leading: true,
          trailing: true,
        }),
        "call_info_header",
      );
      // subscriptionServer.subscribeToQueryReturnsForUser(user?.id, _.throttle(handleQuerySubscription, 5000, { leading: true, trailing: true }), 'call_info_header',)
      subscriptionServer.subscribeToQueryReturnsForUser(
        user?.id,
        handleQuerySubscription,
        "call_info_header",
      );
    }
    return () => {
      // ComponentWillUnmount in Class Component
      // subscriptionServer.unsubscribeToIncident(incident?.id)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.id, onlineState, subscriptionServer, user?.id]);

  const [getDispatchableOfficers] = useLazyQuery(DISPATCHABLE_OFFICERS, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            property: "dispatchableOfficers",
            value: res,
          },
        });
      }
    },
  });

  const [getSpecificSetup, { loading: specificSetupLoading }] = useLazyQuery(
    SPECIFIC_SETUP_LISTS_QUERY,
    {
      context: { serializationKey: "incidentQueue" },
      fetchPolicy: { requestPolicy: "no-cache" },
      variables: { oriId: incident?.ori?.id, originatorId: user?.id },
      onError: error => {
        console.error(JSON.stringify(error));
      },
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
          setSpecificSetup(
            localStoreData(data, `specific-setup-${incident?.ori?.id}`),
          );
        }
      },
    },
  );

  const { refetch: getNarrativeTemplates } = useQuery(GET_NARRATIVE_TEMPLATES, {
    skip: true,
    variables: { originatorId: user?.id },
  });

  //When the incident is opened, check which ORI it's from, check if the setupLists are already stored (specific-setup-oriId)
  //- if not - fetch it and store it, then setSpecificSetup in the SetupContext
  useEffect(() => {
    let oriId = incident?.ori?.id;
    if (localGetData(`specific-setup-${oriId}`)) {
      // console.log('Found setup in localStorage')
      const setup = localGetData(`specific-setup-${oriId}`);
      const now = moment();
      const age = now.diff(setup.timestamp, "hours");
      if (age > 8 && onlineState) {
        // console.log('Setup was too old')
        localStorage.removeItem(`specific-setup-${oriId}`);
        getSpecificSetup();
      } else {
        // console.log('Using localStorage setup in state')
        setSpecificSetup(setup);
      }
    } else if (specificSetupLoading) {
      // console.log('Setup is loading')
    } else {
      // console.log('Did not find setup in localStorage. Fetching it now')
      getSpecificSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.ori?.id, onlineState, specificSetupLoading]);

  useEffect(() => {
    if (
      !incidents[incidentIndex]?.dispatchableOfficers &&
      !incident.id.includes("Offline_")
    ) {
      getDispatchableOfficers({ variables: { incidentId: incident.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id]);

  useEffect(() => {
    // console.log('Setting the incidentPeople')
    let persons = [];
    if (incident?.personIncidentSpecifics?.length > 0) {
      incident?.personIncidentSpecifics?.map(pis => {
        // let person = {
        //   id: pis?.person?.id,
        //   name: `${pis?.person?.personalAttribute?.lastName}, ${pis?.person?.personalAttribute?.firstName}`,
        //   personalAttribute: pis.person.per
        // }
        let person = JSON.parse(JSON.stringify(pis?.person));
        if (person) {
          person.role = pis?.incidentInvolvement?.name;
          person.name = `${pis?.person?.personalAttribute?.lastName}, ${pis?.person?.personalAttribute?.firstName}`;
          persons.push(person);
        }
        return null;
      });
    }
    setIncidentPeople(persons);
  }, [incident?.personIncidentSpecifics]);

  useEffect(() => {
    let officers = [];
    if (incident?.officerIncidentSpecifics?.length > 0) {
      incident.officerIncidentSpecifics.map(ois => {
        if (ois?.officer?.id) {
          officers.push(ois.officer);
          return null;
        }
        return null;
      });
    }
    setIncidentOfficers(officers);
  }, [incident?.officerIncidentSpecifics]);

  useEffect(() => {
    document.title = `Valcour Mobile - ${incident?.incidentNumber || "Incident"}`;
  }, [incident?.incidentNumber]);

  useEffect(() => {
    const fetchNarrativeTemplates = async () => {
      const { id } = user;
      const { id: oriId } = incident?.ori ?? {};

      if (id && oriId) {
        let result = localGetData("narrative-templates-mp");
        if (result) {
          if (result[oriId]) {
            // console.log('Found a result with the oriId key:', result);
          } else {
            // console.log('Found templates but not for the oriId');
            try {
              const response = await getNarrativeTemplates({
                oriId: oriId,
                originatorId: id,
              });
              let mergedResults = {
                ...result,
                ...{ [oriId]: response.data.getNarrativeTemplates },
              };
              await localStoreData(mergedResults, "narrative-templates-mp");
            } catch (error) {
              console.error("Error fetching narrative templates:", error);
            }
          }
        } else {
          // console.log('Fetching narrative templates');
          try {
            const response = await getNarrativeTemplates({
              oriId: oriId,
              originatorId: id,
            });
            // console.log('getNarrativeTemplates response is: ', response.data.getNarrativeTemplates);
            await localStoreData(
              { [oriId]: response.data.getNarrativeTemplates },
              "narrative-templates-mp",
            );
          } catch (error) {
            console.error("Error fetching narrative templates:", error);
          }
        }
      }
    };

    fetchNarrativeTemplates();
  }, [incident?.ori?.id, user, incident?.ori]); // eslint-disable-line react-hooks/exhaustive-deps

  const [removeOfficerFromIncident] = useMutation(REMOVE_OFFICER_FROM_INCIDENT);
  const [addOfficerToIncident] = useMutation(ADD_OR_UPDATE_OFFICER_IN_INCIDENT);

  const [setOfficerIncidentTime] = useMutation(SET_OFFICER_INCIDENT_TIMES);
  const [cancelIncident] = useMutation(CANCEL_INCIDENT);

  const [showVehiclesDetail, setShowVehiclesDetail] = useState(false);
  const [showVehicleSearch, setShowVehicleSearch] = useState(false);

  const [
    getPissForIncident,
    {
      loading: peopleInfoLoading,
      error: peopleInfoError,
      data: peopleInfoData,
    },
  ] = useLazyQuery(PISS_FOR_INCIDENT, {
    context: { serializationKey: "incidentQueue" },
    variables: { incidentId: incident.id, originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        let res = data[Object.keys(data)[0]];
        let incidentId = res.id;
        let incidentUuid = res.uuid;
        //pis will be an array so map over it and send each pis to the dispatch to update it
        let piss = res.personIncidentSpecifics;
        piss.forEach(pis => {
          incidentDispatch({
            type: EXTEND_PIS,
            payload: {
              incidentId: incidentId,
              incidentUuid: incidentUuid,
              pisId: pis.id,
              pisUuid: pis.uuid,
              pis: pis,
            },
          });
        });
      }
    },
  });

  const [
    getVehiclesInfo,
    {
      loading: vehiclesInfoLoading,
      error: vehiclesInfoError,
      data: vehiclesInfoData,
    },
  ] = useLazyQuery(GET_VEHICLE_INCIDENT_SPECIFICS, {
    context: { serializationKey: "incidentQueue" },
    variables: { incidentId: incident.id, originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        let res = data[Object.keys(data)[0]];
        res &&
          res?.length > 0 &&
          incidentDispatch({
            type: UPDATE_INCIDENT,
            payload: {
              id: incident.id,
              uuid: incident.uuid,
              property: "vehicleIncidentSpecifics",
              value: res,
            },
          });
        setShowVehiclesDetail(true);
      }
    },
  });

  const [
    getIncidentDetail,
    { loading: incidentDetailLoading, error: incidentDetailError },
  ] = useLazyQuery(GET_INCIDENT_DETAIL, {
    context: { serializationKey: "incidentQueue" },
    variables: { incidentId: incident.id, originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        let res = data[Object.keys(data)[0]];
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: { incidentIndex: incidentIndex, value: res.incidentDetail },
        });
      }
    },
  });

  const [getArrests] = useLazyQuery(GET_ARRESTS, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (!data) return;
      responseErrorHandler(data);

      const raw = data[Object.keys(data)[0]] || [];

      const normalized = raw.map(arrest => {
        const charges = Array.isArray(arrest?.arrestCharges)
          ? arrest.arrestCharges
          : [];

        // if you want an empty placeholder when length === 0:
        const safeCharges =
          charges.length === 0 ? [createEmptyArrestCharge()] : charges;

        return {
          ...arrest, // new arrest object
          arrestCharges: [...safeCharges], // new array instance
        };
      });

      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          incidentId: incident.id,
          incidentIndex,
          property: "incidentArrests",
          value: normalized,
        },
      });
    },
  });

  // const [getProperties, { loading: propertiesLoading, error: propertiesError }] = useLazyQuery(GET_PROPERTIES,
  //   {
  //     context: { serializationKey: "incidentQueue" },
  //     variables: { originatorId: user?.id },
  //     fetchPolicy: 'network-only',
  //     onError: (error) => { console.error(JSON.stringify(error)) },
  //     onCompleted: (data) => {
  //       if (data) {
  //         responseErrorHandler(data)
  //         let res = data[Object.keys(data)[0]]
  //         incidentDispatch({ type: UPDATE_INCIDENT, payload: { incidentId: incident.id, incidentIndex: incidentIndex, property: 'incidentProperties', value: res } })
  //       }
  //     }
  //   })

  const [
    getTickets,
    { data: ticketsData, loading: ticketsLoading, error: ticketsError },
  ] = useLazyQuery(GET_TICKETS, {
    context: { serializationKey: "incidentQueue" },
    variables: { incidentId: incident?.id, originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        let tickets = JSON.parse(JSON.stringify(data[Object.keys(data)[0]]));

        tickets.forEach((ticket) => {
          if (memoizedCanEditIncident && memoizedIsOnIncident) {
            if (!ticket?.currentStatus || ticket?.currentStatus < 3) {
              let input = {
                incidentId: incidents[incidentIndex]?.id,
                originatorId: user?.id,
                ticketId: ticket.id ? ticket.id : ticket.uuid,
              };
              //set issued at time to incident call time
              if (!ticket?.issuedAt && incidents[incidentIndex]?.callTime) {
                // console.log(`Ticket ${ticket?.id} needs an issuedAt and there is an incident call time`)
                ticket.issuedAt = incidents[incidentIndex]?.callTime;
                input.issuedAt = incidents[incidentIndex]?.callTime;
              }
              // //set the violation date to the incident call time
              if (
                !ticket?.violationDate &&
                incidents[incidentIndex]?.callTime
              ) {
                // console.log(`Ticket ${ticket?.id} needs a violation date and there is an incident call time`)
                ticket.violationDate = incidents[incidentIndex]?.callTime;
                input.violationDate = incidents[incidentIndex]?.callTime;
              }
              // //set issued by to the primary officer
              if (!ticket?.officer) {
                let primaryOis = incidents[
                  incidentIndex
                ].officerIncidentSpecifics?.find(
                  ois => ois?.primaryOfficer === true && !!ois.officer,
                );
                // console.log(`Ticket ${ticket?.id} needs an officer and there ${primaryOis ? "is" : "is not"} a primary officer`)
                if (primaryOis) {
                  ticket.officer = primaryOis.officer;
                  input.officerId = primaryOis.officer.id;
                }
              }
              //set the violation location to the incident address
              if (
                (!ticket?.ticketLocation?.name ||
                  !ticket?.ticketLocation?.streetName) &&
                (incidents[incidentIndex].address?.streetName ||
                  incidents[incidentIndex].address?.state)
              ) {
                // console.log(`Ticket ${ticket?.id} needs a location and there ${(incidents[incidentIndex].address?.streetName || incidents[incidentIndex].address?.state) ? "is" : "is not"} an incident location`)
                ticket.ticketLocation = incidents[incidentIndex].address;
                input.ticketLocationId = incidents[incidentIndex].address.id;
              }

              //if there's only one person on the pis use their mailingAddress if that doesn't exist use their address and set issued to
              if (
                incidents[incidentIndex]?.personIncidentSpecifics?.length ===
                  1 &&
                incidents[incidentIndex].personIncidentSpecifics[0]?.person
                  ?.personalAttribute?.lastName
              ) {
                const person = incidents[incidentIndex]
                  .personIncidentSpecifics[0]?.person?.personalAttribute
                  ?.lastName
                  ? incidents[incidentIndex].personIncidentSpecifics[0]?.person
                  : null;
                if (!ticket?.issuedTo?.id) {
                  // console.log(`Ticket ${ticket?.id} needs an issued to person and there's one person on the incident`)
                  ticket.issuedTo = person;
                  input.issuedToId = person.id;
                }
              }

              //if there's only one vehicle on the vis set it as the vehicle on the ticket
              if (
                !ticket?.vehicle?.vehiclePlate &&
                incidents[incidentIndex]?.vehicleIncidentSpecifics?.length ===
                  1 &&
                incidents[incidentIndex].vehicleIncidentSpecifics[0]?.vehicle
                  ?.vehiclePlate
              ) {
                // console.log(`Ticket ${ticket?.id} needs a vehicle and there's one vehicle on the incident`)
                const vehicle =
                  incidents[incidentIndex].vehicleIncidentSpecifics[0]?.vehicle;
                ticket.vehicle = vehicle;
                input.vehicleId = vehicle.id;
              }

              //if there's a pis with the role "operator" set the ticket operator field
              if (
                !ticket?.operator?.id &&
                incidents[incidentIndex]?.personIncidentSpecifics?.length > 0
              ) {
                // console.log(`Ticket ${ticket?.id} needs an operator there's a person on the incident with that role`)
                const operator = incidents[
                  incidentIndex
                ]?.personIncidentSpecifics?.find(
                  pis => pis?.incidentInvolvement?.name === "Operator",
                )?.person;
                if (operator) {
                  ticket.operator = operator;
                  input.operatorId = operator.id;
                }
              }
              //The input has three default fields - the originator, the ticketId, and the incidentId - if more have been added then the ticket needs to be updated
              Object.keys(input).length > 3 &&
                updateTicketAllFields({
                  variables: { input },
                  context: { serializationKey: "incidentQueue" },
                });
            }
          }
          if (ticket?.currentStatus > 2) {
            ticket = returnLockedTicket(ticket);
          }
        });
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            incidentId: incident.id,
            incidentIndex: incidentIndex,
            property: "incidentTickets",
            value: tickets,
          },
        });
      }
    },
  });

  const [
    getIncidentReview,
    { loading: incidentReviewLoading, error: incidentReviewError },
  ] = useLazyQuery(GET_INCIDENT_REVIEW, {
    context: { serializationKey: "incidentQueue" },
    variables: {
      incidentReviewId: incident?.incidentReview?.id,
      originatorId: user?.id,
    },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        let res = data[Object.keys(data)[0]];
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            incidentId: incident.id,
            incidentIndex: incidentIndex,
            property: "incidentReview",
            value: res,
          },
        });
      }
    },
  });

  const handleChange = panel => (event, newExpanded) => {
    if (newExpanded && panel === "panel7" && !onlineState) {
      if (user?.startingTicketNumber) {
        setIncidentPanel(prevState => {
          return {
            ...prevState,
            [incident?.uuid || incident.id]: newExpanded ? panel : "none",
          };
        });
      } else {
        alert(
          "You have no remaining E-Ticket numbers available. Please use a paper ticket or reconnect to the internet.",
        );
        setIncidentPanel(prevState => {
          return {
            ...prevState,
            [incident?.uuid || incident.id]: newExpanded ? panel : "none",
          };
        });
      }
    } else {
      // if (!onlineState && newExpanded && panel === "panel7") {
      //   alert('Please reconnect before starting an eTicket')
      // } else {

      setIncidentPanel(prevState => {
        return {
          ...prevState,
          [incident?.uuid || incident.id]: newExpanded ? panel : "none",
        };
      });
    }
    // }
  };

  useEffect(() => {
    !incidentPanel[incident?.uuid || incident.id] &&
      setIncidentPanel(prevState => {
        return { ...prevState, [incident.id]: "panel1" };
      });
  });

  const updateIncidentTeam = team => {
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        id: incident?.id || incident?.uuid,
        property: "team",
        value: team,
      },
    });
    updateCallTeam({
      variables: {
        input: {
          incidentId: incident?.id || incident?.uuid,
          originatorId: user?.id,
          teamId: team?.id,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const setOfficerTime = (
    e,
    name,
    passedIncidentIndex = null,
    passedIncidentId = null,
  ) => {
    const momentTime = moment().utc();
    let userOis;
    let localIncidentIndex = passedIncidentIndex || incidentIndex;
    let incidentId = passedIncidentId || incident?.id;
    //check the incident.ois to see if the officer exists
    incidents[localIncidentIndex].officerIncidentSpecifics.forEach((o) => {
      if (
        (o?.officer?.id?.toString() === user?.id?.toString() &&
          !o?.clearedAt) ||
        !o?.officer?.id
      ) {
        userOis = o;
      }
    });

    if (
      userOis?.primaryOfficer &&
      user?.officer?.deptAttribute?.team?.id &&
      !incident?.team?.id
    ) {
      updateIncidentTeam(user?.officer?.deptAttribute?.team);
    }
    // use this to clear the ofc location. If there is no userOis, don't create a new OIS.
    if (name === "clearedAt" && userOis && user?.id) {
      setAvailOfficer({
        variables: {
          input: {
            originatorId: user?.id,
            officerId: user?.id,
            secondaryAddress: "",
          },
        },
      });
      availableOfficerDispatch({
        type: UPDATE_AVAILABLE_OFFICER,
        payload: {
          officerId: user?.id,
          property: "officerLocation",
          value: "",
        },
      });
    }

    //check all the previous times - if they are not set, set them to serverTimeFormat as well
    if (userOis) {
      updateOfficerTimesInOis(
        userOis,
        name,
        momentTime,
        user?.officer,
        setup?.setupLists?.logCodes,
        incidentDispatch,
        activeIncidentDispatch,
        incidentId,
        setStatus,
        user?.id,
        setOfficerIncidentTime,
        addOfficerToIncident,
      );

      //side effects for incident review and detail
      if (
        (userOis?.primaryOfficer &&
          incidents[incidentIndex]?.incidentReview?.investigatingOfficer?.id !==
            user?.id) ||
        incidents[incidentIndex]?.incidentDetail?.investigatingOfficer?.id !==
          user?.id
      ) {
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: {
            incidentDetailId: incidents[incidentIndex]?.incidentDetail?.id,
            incidentIndex: incidentIndex,
            property: "investigatingOfficer",
            value: user?.officer,
          },
        });
        incidentDispatch({
          type: UPDATE_INCIDENT_REVIEW,
          payload: {
            incidentIndex: incidentIndex,
            property: "investigatingOfficer",
            value: user?.officer,
          },
        });
        updateIncidentReview({
          variables: {
            input: {
              incidentReviewId: incidents[incidentIndex]?.incidentReview?.id,
              originatorId: user?.id,
              incidentId: incident.id,
              investigatingOfficerId: user?.id,
              reviewStatusId: setup.setupLists.reviewStatuses?.find(
                r => r?.name === "Pending",
              )?.id,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
        updateIncidentDetail({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incident.id,
              incidentDetailId: incidents[incidentIndex]?.incidentDetail?.id,
              investigatingOfficerId: user?.id,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      }
    } else if (name !== "clearedAt") {
      let oisUuid = handleUuid();
      let localOis = JSON.parse(JSON.stringify(emptyOis));
      localOis.id = oisUuid;
      localOis.uuid = oisUuid;
      localOis.incidentId = incident.id;
      updateOfficerTimesInOis(
        localOis,
        name,
        momentTime,
        user?.officer,
        setup?.setupLists?.logCodes,
        incidentDispatch,
        activeIncidentDispatch,
        incidentId,
        setStatus,
        user?.id,
        setOfficerIncidentTime,
        addOfficerToIncident,
      );
    }
  };

  const handleCancelIncident = () => {
    if (
      window.confirm(
        `Do you want to ${
          incident?.inactive ? "restore" : "cancel"
        } this incident?`,
      )
    ) {
      activeIncidentDispatch({
        type: REMOVE_ACTIVE_INCIDENT,
        payload: incident.id,
      });
      cancelIncident({
        variables: {
          input: {
            incidentId: incident.id,
            originatorId: user?.id,
            cancel: !incident?.inactive,
          },
        },
      });
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incident.id,
          property: "inactive",
          value: !incident?.inactive,
        },
      });
    } else {
      return null;
    }
  };

  const callType = incident?.classification?.name;

  const peopleText = useMemo(() => {
    let text = "People Detail";
    if (incident?.personIncidentSpecifics?.length) {
      text = text + " - ";
      incident?.personIncidentSpecifics?.forEach((pis, index) => {
        const offensePerson = yoeJuvenileStatuses.find(
          pos => pos?.person?.id?.toString() === pis?.person?.id?.toString(),
        );
        text =
          text +
          personPanelHeader(
            pis?.person,
            incident?.personIncidentSpecifics?.length === index + 1,
            pis?.sealed,
            offensePerson?.isJuvenile,
            offensePerson?.isYoeEligible && pis?.yoe,
          );
      });
      return text;
    } else {
      return text;
    }
  }, [incident?.personIncidentSpecifics, yoeJuvenileStatuses]);

  const vehicleText = viss => {
    let text = "Vehicles";
    if (viss) {
      text = text + " - ";
      viss.forEach(
        (vis, index) =>
          (text =
            text + vehiclePanelHeader(vis?.vehicle, viss.length === index + 1)),
      );
      return text;
    } else {
      return text;
    }
  };

  const incidentDetailText = (offenses, officer) => {
    let text = "Incident Detail";
    if (offenses) {
      let beginAdd = false;
      offenses.forEach((offense, index) => {
        if (offense?.offenseViolationPrototype?.offenseCategory?.name) {
          if (!beginAdd) text = text + " - [[";
          beginAdd = true;
          text =
            text +
            `${offense?.offenseViolationPrototype?.offenseCategory?.name}${
              offenses.length !== index + 1 ? ", " : ""
            }`;
        }
      });
      if (beginAdd) text = text + "]]";
    }
    if (officer) {
      text =
        text +
        ": " +
        officer?.deptAttribute?.ori?.abbreviation +
        "-" +
        officer?.deptAttribute?.badge +
        ": " +
        officer?.personalAttribute?.lastName +
        ", " +
        officer?.personalAttribute?.firstName;
    }
    return text;
  };

  const arresteePersonSealed = (arrestee, piss) => {
    if (!arrestee) return false;
    let pis = piss?.find(pis => pis?.person?.id === arrestee?.id);
    return pis?.sealed;
  };

  const arrestInformationText = useCallback((incident, yoeJuvenileStatuses) => {
    const { incidentArrests, personIncidentSpecifics } = incident || [];
    let text = "Arrest Information";
    if (incidentArrests) {
      text = text + " - ";
      incidentArrests.forEach((arrest, index) => {
        const isSealed = arresteePersonSealed(
          arrest?.arrestee,
          personIncidentSpecifics,
        );
        const arresteeOffenseStatus = yoeJuvenileStatuses.find(pos => {
          return pos?.person?.id === arrest?.arrestee?.id;
        });
        let pis = personIncidentSpecifics?.find(
          pis => pis?.person?.id === arrest?.arrestee?.id,
        );
        text =
          text +
          arrestPanelHeader(
            arrest?.arrestee,
            incidentArrests.length === index + 1,
            isSealed,
            arresteeOffenseStatus?.isJuvenile,
            arresteeOffenseStatus?.isYoeEligible && pis?.yoe,
          );
      });
    }
    return text;
  }, []);

  const arrestInformationTextMemo = useMemo(() => {
    return arrestInformationText(incident, yoeJuvenileStatuses);
  }, [arrestInformationText, incident, yoeJuvenileStatuses]);

  // const propertyInformationHeader = (incidentProperties) => {
  //   if (!incidentProperties) return ""
  //   let count = 0
  //   incidentProperties.forEach(prop => !checkForEmptyPropertyObject(prop) && count++)
  //   return count > 0 ? count : ""
  // }

  const ticketsHeader = useMemo(() => {
    let text = "Tickets & Warnings";
    if (incident?.incidentTickets && incident?.incidentTickets?.length > 0) {
      text = text + " - " + ticketPanelHeader(incident?.incidentTickets);
    }
    return text;
  }, [incident?.incidentTickets]);

  const reviewHeader = incidentReview => {
    let text = "Incident Review";
    if (incidentReview) {
      text = text + " - " + reviewPanelHeader(incident?.incidentReview);
    }
    return text;
  };

  const handleArrestFrameFetch = (fromSubServer = false) => {
    if (
      fromSubServer ||
      (incidentPanel[incident?.uuid || incident.id] !== "panel5" &&
        (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true"))
    ) {
      getArrests({ variables: { incidentId: incident?.id } });
      getIncidentDetail({ variables: { incidentId: incident?.id } });
    }
  };

  const selfOiss = incident?.officerIncidentSpecifics?.filter(
    ois => ois?.officer?.id?.toString() === user?.id.toString(),
  );
  const selfOis = selfOiss?.[selfOiss.length - 1];

  const handleNarrativeLockMessage = message => {
    // console.log("Narrative lock message received: ", message);
    const { narrativeId, action, originatorId, app } =
      parseNarrativeSubscriptionMessage(message);
    const isSelf =
      originatorId?.toString() === user?.id.toString()
        ? app === "mobile"
        : false;
    let popperMessage = "";
    const segment = incident?.narrativeSegments?.find(
      seg => seg.id?.toString() === narrativeId?.toString(),
    );
    const segmentBeginning = segment?.content
      ? segment.content.substring(0, 30) +
        (segment.content?.length > 30 ? "..." : "")
      : "";

    if (action === "edit_requested") {
      popperMessage = `Another user has requested to edit the narrative starting with '${segmentBeginning} in ${incident?.incidentNumber}.`;
    }
    if (action === "edit_initiated") {
      popperMessage = `Another user has initiated editing the narrative starting with '${segmentBeginning} in ${incident?.incidentNumber}.`;
    }
    if (action === "edit_cleared") {
      popperMessage = `In ${incident?.incidentNumber} the edit lock was cleared for the narrative starting with '${segmentBeginning}'`;
    }

    if (!isSelf) setPopper({ open: true, content: popperMessage });
  };

  const handleNarrativeSegmentSubscription = (segmentId, action) => {
    if (subscriptionServer) {
      if (action === "unsubscribe") {
        subscriptionServer.unsubscribeFromNarrativeSegment(segmentId);
      } else {
        subscriptionServer.subscribeToNarrativeSegment(segmentId, message =>
          handleNarrativeLockMessage(message),
        );
      }
    }
  };

  if (incident.blocked) {
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <h3 style={{ color: "red" }}>
          You are not authorized to access this incident (403).
        </h3>
      </div>
    );
  }
  return (
    <div>
      <CssBaseline />
      {hasSealedIncidentRecord(incident) && <SealedIncidentBanner />}
      <IncidentHeader
        setPopper={setPopper}
        canEdit={memoizedCanEditIncident}
        incidentNumber={incidentNumber}
        incidentId={incident.id}
        incidentIndex={incidentIndex}
        incidentAddress={incident?.address}
        setOfficerTime={setOfficerTime}
        handleCancelIncident={handleCancelIncident}
        handlePersonOpening={handlePersonOpening}
        handleIncidentOpening={handleIncidentOpening}
        handleTicketOpening={handleTicketOpening}
        handleVehicleOpening={handleVehicleOpening}
        inactive={incident?.inactive}
        user={user}
        userRoles={user?.roleNames}
        linkedIncidents={incident.linkedIncidents}
        setShowQueryModal={setShowQueryModal}
        selfOis={selfOis}
        chatWindowRef={chatWindowRef}
        openChat={openChat}
        incident={incident}
        userPreferences={userPreferences}
        setUserPreferences={setUserPreferences}
        showVehicleSearch={showVehicleSearch}
        setShowVehicleSearch={setShowVehicleSearch}
        incidentPeople={incidentPeople}
        subscriptionServer={subscriptionServer}
        isOnIncident={memoizedIsOnIncident}
        socketConnected={socketConnected}
        setPausePolling={setPausePolling}
      />
      <div>
        <StyledAccordion
          nightmode={nightMode?.toString()}
          square
          expanded={incidentPanel[incident?.uuid || incident.id] === "panel1"}
          onChange={handleChange("panel1")}
          onClick={() =>
            incidentPanel[incident?.uuid || incident.id] !== "panel1" &&
            (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") &&
            getCallInfo({ variables: { incidentId: incident?.id } })
          }
          TransitionProps={{ unmountOnExit: true }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            style={{ margin: 0 }}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography
                className={classes.expansionPanelTypography}
                data-cy="initial-call-information-panel-header"
              >
                Initial Call Information -{" "}
                {callType ? "[" + callType + "]" : null}{" "}
                {moment
                  .utc(incident?.callTime, "YYYY-MM-DD HH:mm:ss UTC")
                  .local()
                  .format("MM/DD/YYYY HH:mm")}{" "}
                {incident?.address?.name
                  ? ": " + incident?.address?.name
                  : null}
              </Typography>
              <HelpIconButton objectname="mobile_initial_call_info" />
            </Box>
          </StyledAccordionSummary>
          <StyledAccordionDetails onClick={e => e.stopPropagation()}>
            <InitialCallInformation
              canEdit={memoizedCanEditIncident}
              removeOfficerFromIncident={removeOfficerFromIncident}
              setup={setup}
              incident={incident}
              user={user}
              incidentId={incident.id}
              incidentNumber={incidentNumber}
              incidentIndex={incidentIndex}
              userRoles={user?.roleNames}
              setPausePolling={setPausePolling}
              handleIncidentOpening={handleIncidentOpening}
              handleTicketOpening={handleTicketOpening}
              handlePersonOpening={handlePersonOpening}
              _initialCallInfoIsMounted={_initialCallInfoIsMounted}
              setPopper={setPopper}
              getPissForIncident={getPissForIncident}
              memoizedIsOnIncident={memoizedIsOnIncident}
              incidentPeople={incidentPeople}
              handleNarrativeSegmentSubscription={
                handleNarrativeSegmentSubscription
              }
              subscriptionServer={subscriptionServer}
            />
          </StyledAccordionDetails>
        </StyledAccordion>
        <StyledAccordion
          nightmode={nightMode?.toString()}
          square
          expanded={incidentPanel[incident?.uuid || incident.id] === "panel2"}
          onChange={handleChange("panel2")}
          onClick={() =>
            incidentPanel[incident?.uuid || incident.id] !== "panel2" &&
            (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") &&
            (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") &&
            getPissForIncident()
          }
          TransitionProps={{ unmountOnExit: true }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            aria-controls="panel2d-content"
            id="panel2d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography
                className={classes.expansionPanelTypography}
                data-cy="people-detail-panel-header"
              >
                {peopleText}
              </Typography>
              <HelpIconButton objectname="mobile_people" />
            </Box>
          </StyledAccordionSummary>
          <StyledAccordionDetails onClick={e => e.stopPropagation()}>
            <PeopleDetail
              _peopleDetailIsMounted={_peopleDetailIsMounted}
              setPopper={setPopper}
              piss={incident?.personIncidentSpecifics || []}
              incidentIndex={incidentIndex}
              incidentId={incident?.id}
              incidentUuid={incident?.uuid}
              incidentCallTime={incident?.callTime}
              primaryOfficerId={incident?.officerIncidentSpecifics?.find(
                ois => ois.primaryOfficer,
              )?.officer?.id}
              peopleInfoLoading={peopleInfoLoading}
              peopleInfoData={peopleInfoData}
              peopleInfoError={peopleInfoError}
              incidentOriId={incident?.ori?.id}
              handlePersonOpening={handlePersonOpening}
              handleIncidentOpening={handleIncidentOpening}
              handleTicketOpening={handleTicketOpening}
              userRoles={user?.roleNames}
              canEdit={memoizedCanEditIncident}
              setPausePolling={setPausePolling}
              getPissForIncident={getPissForIncident}
              incidentPeople={incidentPeople}
            />
          </StyledAccordionDetails>
        </StyledAccordion>

        <StyledAccordion
          nightmode={nightMode?.toString()}
          square
          expanded={incidentPanel[incident?.uuid || incident.id] === "panel3"}
          onChange={handleChange("panel3")}
          onClick={() =>
            incidentPanel[incident?.uuid || incident.id] !== "panel3" &&
            (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") &&
            getVehiclesInfo()
          }
          TransitionProps={{ unmountOnExit: true }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            aria-controls="panel3d-content"
            id="panel3d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography className={classes.expansionPanelTypography}>
                {vehicleText(incident?.vehicleIncidentSpecifics)}
              </Typography>
              <HelpIconButton objectname="mobile_vehicle" />
            </Box>
          </StyledAccordionSummary>
          <StyledAccordionDetails onClick={e => e.stopPropagation()}>
            <VehiclesDetail
              _vehicleDetailIsMounted={_vehicleDetailIsMounted}
              setPopper={setPopper}
              viss={incident?.vehicleIncidentSpecifics || []}
              piss={incident?.personIncidentSpecifics || []}
              incidentIndex={incidentIndex}
              incidentId={incident?.id}
              incidentUuid={incident?.uuid}
              vehiclesInfoLoading={vehiclesInfoLoading}
              vehiclesInfoData={vehiclesInfoData}
              vehiclesInfoError={vehiclesInfoError}
              showVehiclesDetail={showVehiclesDetail}
              incidentOriId={incident?.ori?.id}
              userRoles={user?.roleNames}
              canEdit={memoizedCanEditIncident}
              showVehicleSearch={showVehicleSearch}
              setShowVehicleSearch={setShowVehicleSearch}
            />
          </StyledAccordionDetails>
        </StyledAccordion>
        {/* <StyledAccordion nightmode={nightMode?.toString()} square expanded={incidentPanel[incident?.uuid || incident.id] === 'panel4'} onChange={handleChange('panel4')} onClick={() => {(onlineState || import.meta.env.VITE_FORCE_ONLINE  === "true") && getIncidentDetail({ variables: { incidentDetailId: incident?.incidentDetailId } }); }} TransitionProps={{ unmountOnExit: true }}> */}
        <StyledAccordion
          nightmode={nightMode?.toString()}
          id="incident-detail-styled-accordion"
          square
          expanded={incidentPanel[incident?.uuid || incident.id] === "panel4"}
          onChange={handleChange("panel4")}
          onClick={() => {
            incidentPanel[incident?.uuid || incident.id] !== "panel4" &&
              (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") &&
              getIncidentDetail();
          }}
          TransitionProps={{ unmountOnExit: true }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            aria-controls="panel4d-content"
            id="panel4d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography className={classes.expansionPanelTypography}>
                {incidentDetailText(
                  incident?.incidentDetail?.incidentOffenses,
                  incident?.incidentDetail?.investigatingOfficer,
                )}
              </Typography>
              <HelpIconButton objectname="mobile_incident_detail" />
            </Box>
          </StyledAccordionSummary>
          <StyledAccordionDetails
            id="incident-detail-styled-accordion-details"
            onClick={e => e.stopPropagation()}
          >
            <IncidentDetail
              // offenseViolationData={offenseViolationData}
              piss={incident?.personIncidentSpecifics || []}
              incidentIndex={incidentIndex}
              incidentId={incident?.id}
              incidentUuid={incident?.uuid}
              incidentDetail={incident?.incidentDetail || null}
              incidentOriId={incident?.ori?.id}
              incident={incident}
              incidentDetailError={incidentDetailError}
              incidentDetailLoading={incidentDetailLoading}
              primaryOfficer={
                incident?.officerIncidentSpecifics?.find(
                  ois => ois.primaryOfficer,
                )?.officer
              }
              userRoles={user?.roleNames}
              canEdit={memoizedCanEditIncident}
              canEditNarrative={memoizedCanEditOfficerNarrative}
              memoizedIsOnIncident={memoizedIsOnIncident}
              _incidentDetailIsMounted={_incidentDetailIsMounted}
              setPausePolling={setPausePolling}
              setPopper={setPopper}
            />
          </StyledAccordionDetails>
        </StyledAccordion>

        <StyledAccordion
          nightmode={nightMode?.toString()}
          square
          expanded={incidentPanel[incident?.uuid || incident.id] === "panel5"}
          onChange={handleChange("panel5")}
          onClick={handleArrestFrameFetch}
          TransitionProps={{ unmountOnExit: true }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            aria-controls="panel5d-content"
            id="panel5d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography className={classes.expansionPanelTypography}>
                {arrestInformationTextMemo}
              </Typography>
              <HelpIconButton objectname="mobile_arrest_info" />
            </Box>
          </StyledAccordionSummary>
          <StyledAccordionDetails onClick={e => e.stopPropagation()}>
            <ArrestInformationTabs
              incidentOfficers={incidentOfficers}
              incidentIndex={incidentIndex}
              incidentId={incident?.id}
              incidentUuid={incident?.uuid}
              incidentArrests={incident?.incidentArrests || []}
              piss={incident?.personIncidentSpecifics || []}
              incidentOriId={incident?.ori?.id}
              incident={incident}
              userRoles={user?.roleNames}
              canEdit={memoizedCanEditIncident}
              setPausePolling={setPausePolling}
              _arrestInformationIsMounted={_arrestInformationIsMounted}
              getPissForIncident={getPissForIncident}
            />
          </StyledAccordionDetails>
        </StyledAccordion>
        {/* <StyledAccordion nightmode={nightMode?.toString()} square expanded={incidentPanel[incident?.uuid || incident.id] === 'panel6'} onChange={handleChange('panel6')} onClick={() => { incidentPanel[incident?.uuid || incident.id] !== 'panel6' &&  (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") && getProperties({ variables: { incidentId: incident?.id } }) }} TransitionProps={{ unmountOnExit: true }}>
          <StyledAccordionSummary nightmode={nightMode?.toString()} aria-controls="panel6d-content" id="panel6d-header">
            <Typography className={classes.expansionPanelTypography}>Property Information - {propertyInformationHeader(incident.incidentProperties)}</Typography>
          </StyledAccordionSummary>
          <StyledAccordionDetails onClick={e => e.stopPropagation()}>
            // <PropertiesTable
              incidentIndex={incidentIndex}
              incidentId={incident?.id}
              incidentUuid={incident?.uuid}
              incidentProperties={incident?.incidentProperties || null}
              propertiesLoading={propertiesLoading}
              propertiesError={propertiesError}
              piss={incident?.personIncidentSpecifics || []}
              incidentOriId={incident?.ori?.id}
              userRoles={user?.roleNames}
              canEdit={memoizedCanEditIncident}
            />
          </StyledAccordionDetails>
        </StyledAccordion> */}
        {sizeAndWeightEnabledForIncident && sizeAndWeightEnabledForUser && (
          <CustomFrameWrapper
            nightMode={nightMode}
            expanded={incidentPanel[incident?.uuid || incident.id] === "panel9"}
            expansionHandler={handleChange("panel9")}
            typographyClassname={classes.expansionPanelTypography}
            title={`Size and Weight - ${
              incident?.sizeAndWeight?.endedAt
                ? moment
                    .utc(
                      incident?.sizeAndWeight?.endedAt,
                      "YYYY-MM-DD HH:mm:ss UTC",
                    )
                    .local()
                    .format("MM/DD/YYYY HH:mm")
                : ""
            }`}
            panelName={"panel9"}
            helpIconName={"mobile_size_and_weight"}
          >
            <LazyFetchComponent
              hookKey={incident?.sizeAndWeight?.id ? "query" : "mutation"}
              hook={incident?.sizeAndWeight?.id ? useLazyQuery : useMutation}
              query={
                incident?.sizeAndWeight?.id
                  ? GET_SIZE_AND_WEIGHT
                  : ADD_OR_UPDATE_SIZE_AND_WEIGHT_IN_INCIDENT
              }
              variables={
                incident?.sizeAndWeight?.id
                  ? {
                      sizeAndWeightId: incident?.sizeAndWeight?.id,
                      originatorId: user?.id,
                    }
                  : {
                      input: {
                        originatorId: user?.id,
                        incidentId: incident?.id,
                      },
                    }
              }
            >
              {props => (
                <SizeAndWeightFrame
                  incidentNumber={incidentNumber}
                  userRoles={user?.roleNames}
                  userId={user?.id}
                  editableOriIds={user?.editableOriIds}
                  canEdit={memoizedCanEditIncident}
                  incident={incident}
                  people={incidentPeople}
                  piss={incident?.personIncidentSpecifics || []}
                  viss={incident?.vehicleIncidentSpecifics || []}
                  incidentTickets={incident?.incidentTickets || []}
                  incidentOfficers={incidentOfficers}
                  primaryOfficer={findPrimaryOfficer(
                    incident?.officerIncidentSpecifics,
                  )}
                  setPausePolling={setPausePolling}
                  nightMode={nightMode}
                  {...props}
                />
              )}
            </LazyFetchComponent>
          </CustomFrameWrapper>
        )}
        <StyledAccordion
          nightmode={nightMode?.toString()}
          square
          expanded={incidentPanel[incident?.uuid || incident.id] === "panel7"}
          onChange={handleChange("panel7")}
          onClick={() => {
            incidentPanel[incident?.uuid || incident.id] !== "panel7" &&
              (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") &&
              getTickets();
          }}
          TransitionProps={{ unmountOnExit: true }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            aria-controls="panel7d-content"
            id="panel7d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography className={classes.expansionPanelTypography}>
                {ticketsHeader}
              </Typography>
              <HelpIconButton objectname="mobile_tickets_warning" />
            </Box>
          </StyledAccordionSummary>
          <StyledAccordionDetails onClick={e => e.stopPropagation()}>
            <TicketsAndWarningsTabs
              setPopper={setPopper}
              incidentIndex={incidentIndex}
              incidentId={incident?.id}
              incidentUuid={incident?.uuid}
              incidentTickets={incident?.incidentTickets || []}
              ticketsLoading={ticketsLoading}
              ticketsError={ticketsError}
              ticketsData={ticketsData}
              piss={incident?.personIncidentSpecifics || []}
              viss={incident?.vehicleIncidentSpecifics || []}
              incidentPeople={incidentPeople}
              incidentOfficers={incidentOfficers}
              primaryOfficer={
                incident?.officerIncidentSpecifics?.find(
                  ois => ois.primaryOfficer,
                )?.officer
              }
              // vehiclesInfoLoading={vehiclesInfoLoading}
              // vehiclesInfoData={vehiclesInfoData}
              // vehiclesInfoError={vehiclesInfoError}
              incidentOriId={incident?.ori?.id}
              getVehiclesInfo={getVehiclesInfo}
              userRoles={user?.roleNames}
              canEdit={memoizedCanEditIncident}
              canSubmitToJb={memoizedCanSubmitToJb}
              setPausePolling={setPausePolling}
              _ticketAndWarningIsMounted={_ticketAndWarningIsMounted}
              memoizedIsOnIncident={memoizedIsOnIncident}
            />
          </StyledAccordionDetails>
        </StyledAccordion>
        <StyledAccordion
          nightmode={nightMode?.toString()}
          square
          expanded={incidentPanel[incident?.uuid || incident.id] === "panel8"}
          onChange={handleChange("panel8")}
          onClick={() => {
            incidentPanel[incident?.uuid || incident.id] !== "panel8" &&
              (onlineState || import.meta.env.VITE_FORCE_ONLINE === "true") &&
              getIncidentReview();
          }}
          TransitionProps={{ unmountOnExit: true }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            aria-controls="panel8d-content"
            id="panel8d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography className={classes.expansionPanelTypography}>
                {reviewHeader(incident?.incidentReview)}
              </Typography>
              <HelpIconButton objectname="mobile_incident_review" />
            </Box>
          </StyledAccordionSummary>
          <StyledAccordionDetails onClick={e => e.stopPropagation()}>
            <IncidentReview
              incidentIndex={incidentIndex}
              incidentId={incident?.id}
              incidentUuid={incident?.uuid}
              incidentReviewLoading={incidentReviewLoading}
              incidentReviewError={incidentReviewError}
              incidentReview={incident?.incidentReview}
              incidentOriId={incident?.ori?.id}
              userRoles={user?.roleNames}
              canEdit={memoizedCanEditIncident}
              incident={incident}
              setPausePolling={setPausePolling}
              incidentPeople={incidentPeople}
              _incidentReviewIsMounted={_incidentReviewIsMounted}
              sizeAndWeightEnabledForIncident={sizeAndWeightEnabledForIncident}
              axonEnabledForIncident={axonEnabledForIncident}
              setPopper={setPopper}
            />
          </StyledAccordionDetails>
        </StyledAccordion>
      </div>
      <DmvQueryModal
        setPopper={setPopper}
        modalOpen={showQueryModal}
        setModalOpen={setShowQueryModal}
        incidentId={incident.id}
        setPausePolling={setPausePolling}
      />
    </div>
  );
};

const CustomFrameWrapper = ({
  children,
  nightMode = false,
  expanded,
  expansionHandler,
  onClick,
  typographyClassname,
  title,
  panelName,
  helpIconName,
}) => {
  const nightModeStr = nightMode?.toString();
  return (
    <StyledAccordion
      nightmode={nightModeStr}
      square
      expanded={expanded}
      onChange={expansionHandler}
      onClick={onClick}
      TransitionProps={{ unmountOnExit: true }}
    >
      <StyledAccordionSummary
        nightmode={nightModeStr}
        aria-controls={panelName + "d-content"}
        id={panelName + "d-header"}
      >
        <Box flexGrow="1" display="flex" justifyContent="space-between">
          <Typography className={typographyClassname}>{title}</Typography>
          <HelpIconButton objectname={helpIconName} />
        </Box>
      </StyledAccordionSummary>
      <StyledAccordionDetails onClick={e => e.stopPropagation()}>
        {children}
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};
export default IncidentPanels;
